/**
 * Type Catalog admin — Phase 1A.
 * List + create + delete compressor types. FAD table editing is a
 * simple JSON textarea here; a friendlier table editor lands in 1B.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpenText, ArrowLeft, Plus, Trash, Spinner, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  seqListTypes, seqCreateType, seqDeleteType,
} from "@/lib/sequencingApi";
import { useT } from "@/lib/i18n";

export default function SequencingTypesPage() {
  const t = useT();
  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr]       = useState("");
  const [showCreate, setShowCreate] = useState(false);

  async function refresh() {
    setLoading(true); setErr("");
    try {
      const data = await seqListTypes();
      setItems(data.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || String(e));
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="seq-types-page">
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 md:px-6 py-3 flex items-center gap-3">
        <Link to="/sequencing" data-testid="seq-types-back"
              className="w-8 h-8 inline-flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500">
          <ArrowLeft size={16} weight="bold" />
        </Link>
        <BookOpenText size={20} weight="fill" className="text-blue-700 shrink-0" />
        <h1 className="flex-1 text-base md:text-lg font-bold font-['Chivo'] text-slate-900 truncate">
          {t("seq.types_title", "Type catalog")}
        </h1>
        <button type="button" onClick={() => setShowCreate(true)} data-testid="seq-types-create-open"
                className="text-xs font-bold uppercase tracking-wider px-3 h-9 rounded-sm bg-blue-700 text-white hover:bg-blue-800 inline-flex items-center gap-1.5">
          <Plus size={12} weight="bold" /> {t("seq.types_new", "New type")}
        </button>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-6 py-6">
        {err && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-sm">{err}</div>}
        {loading ? (
          <div className="py-10 text-center"><Spinner size={20} className="mx-auto animate-spin text-slate-400" /></div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-sm p-8 text-center text-slate-500">
            <BookOpenText size={28} weight="fill" className="mx-auto text-slate-300 mb-2" />
            <p className="text-sm font-medium">{t("seq.types_empty", "No types defined yet")}</p>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="seq-types-list">
            {items.map(it => (
              <li key={it.id}
                  className="bg-white border border-slate-200 rounded-sm p-3 flex items-center gap-3 hover:border-blue-300"
                  data-testid={`seq-types-row-${it.type_code}`}>
                <div className="flex-1">
                  <div className="text-sm font-bold text-slate-900 font-mono">{it.type_code}</div>
                  <div className="text-[11px] text-slate-500">{it.name} · {(it.fad_table || []).length} rows</div>
                </div>
                <button type="button"
                        onClick={async () => {
                          if (!window.confirm(`Delete type "${it.type_code}"?`)) return;
                          try { await seqDeleteType(it.type_code); toast.success(t("seq.types_deleted", "Type deleted")); refresh(); }
                          catch (e) { toast.error(e?.response?.data?.detail || e?.message); }
                        }}
                        data-testid={`seq-types-delete-${it.type_code}`}
                        className="w-8 h-8 inline-flex items-center justify-center rounded-sm text-slate-400 hover:text-red-700 hover:bg-red-50">
                  <Trash size={14} weight="bold" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </main>

      {showCreate && (
        <CreateTypeModal onClose={() => setShowCreate(false)}
                         onCreated={() => { setShowCreate(false); refresh(); }} />
      )}
    </div>
  );
}

function CreateTypeModal({ onClose, onCreated }) {
  const t = useT();
  const [typeCode, setTypeCode] = useState("");
  const [name, setName]         = useState("");
  const [fadJson, setFadJson]   = useState('[\n  {"pset_bar": 7.0, "fad_m3_per_min": 5.4},\n  {"pset_bar": 8.0, "fad_m3_per_min": 5.0},\n  {"pset_bar": 10.0, "fad_m3_per_min": 4.4}\n]');
  const [busy, setBusy]         = useState(false);

  async function submit(e) {
    e?.preventDefault?.();
    if (!typeCode.trim()) return;
    let fad;
    try { fad = JSON.parse(fadJson); }
    catch { toast.error("FAD table must be valid JSON array"); return; }
    if (!Array.isArray(fad)) { toast.error("FAD table must be a JSON array"); return; }
    setBusy(true);
    try {
      await seqCreateType({
        type_code: typeCode.trim().toUpperCase(),
        name: name.trim() || typeCode.trim().toUpperCase(),
        fad_table: fad,
      });
      toast.success(t("seq.types_created", "Type created"));
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || String(e));
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-30 bg-black/50 flex items-end sm:items-center justify-center p-3"
         data-testid="seq-types-create-modal">
      <form onSubmit={submit} className="bg-white rounded-sm w-full max-w-lg p-5 space-y-3 max-h-[95vh] overflow-y-auto">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-bold font-['Chivo'] text-slate-900 flex-1">{t("seq.types_new", "New type")}</h2>
          <button type="button" onClick={onClose} data-testid="seq-types-create-close"
                  className="w-7 h-7 inline-flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-400">
            <X size={14} weight="bold" />
          </button>
        </div>

        <label className="block">
          <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">Type code</span>
          <input value={typeCode} onChange={e => setTypeCode(e.target.value.toUpperCase())} required autoFocus
                 placeholder="e.g. US40"
                 data-testid="seq-types-field-code"
                 className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm font-mono focus:border-blue-500 focus:outline-none" />
        </label>
        <label className="block">
          <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">Display name</span>
          <input value={name} onChange={e => setName(e.target.value)}
                 data-testid="seq-types-field-name"
                 className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-blue-500 focus:outline-none" />
        </label>
        <label className="block">
          <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">FAD@Pset table (JSON)</span>
          <textarea value={fadJson} onChange={e => setFadJson(e.target.value)} rows={8}
                    data-testid="seq-types-field-fad"
                    className="w-full px-3 py-2 text-[11px] border border-slate-300 rounded-sm font-mono focus:border-blue-500 focus:outline-none resize-none" />
        </label>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} data-testid="seq-types-create-cancel"
                  className="text-xs font-bold uppercase tracking-wider px-3 h-9 rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50">
            {t("common.cancel", "Cancel")}
          </button>
          <button type="submit" disabled={busy || !typeCode.trim()}
                  data-testid="seq-types-create-submit"
                  className="text-xs font-bold uppercase tracking-wider px-4 h-9 rounded-sm bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50 inline-flex items-center gap-1.5">
            {busy ? <Spinner size={12} className="animate-spin" /> : <Plus size={12} weight="bold" />}
            {t("seq.create", "Create")}
          </button>
        </div>
      </form>
    </div>
  );
}
